/* ============================================================================
   SecureLock ID — Pure HTML interactions
   Mirrors all client behaviors from the original Next.js landing page:
     - Mobile menu toggle (hero nav)
     - Flash sale countdown (hero & sticky bar)
     - Mobile sticky bar show/hide on scroll
     - FAQ accordion (faq-and-whatsapp)
     - Live order ticker rotation
     - Customer service hub: tooltip, panel, WhatsApp bubble
     - Anchor link smooth scroll
   ============================================================================ */

(() => {
  'use strict';

  /* ---------------------------------------------------------------- helpers */
  const $  = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));

  const onReady = (fn) => {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  };

  /* --------------------------------------------------- mobile menu (hero)  */
  function initMobileMenu() {
    const btn   = $('[data-mobile-menu-btn]');
    const panel = $('[data-mobile-menu-panel]');
    if (!btn || !panel) return;

    const setOpen = (open) => {
      btn.setAttribute('aria-expanded', String(open));
      panel.dataset.open = String(open);
      btn.innerHTML = open
        ? '<svg class="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>'
        : '<svg class="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>';
    };

    btn.addEventListener('click', () => {
      setOpen(panel.dataset.open !== 'true');
    });
    $$('[data-mobile-menu-close]', panel).forEach((el) => {
      el.addEventListener('click', () => setOpen(false));
    });
  }

  /* ------------------------------------------------- flash sale countdown  */
  function initCountdowns() {
    const pad = (n) => String(n).padStart(2, '0');
    const tick = (state) => {
      let { d, h, m, s } = state;
      if (s > 0) s--;
      else if (m > 0) { m--; s = 59; }
      else if (h > 0) { h--; m = 59; s = 59; }
      else if (d > 0) { d--; h = 23; m = 59; s = 59; }
      return { d, h, m, s };
    };

    $$('[data-countdown]').forEach((root) => {
      const startAttr = root.getAttribute('data-start');
      let state;
      try {
        state = startAttr ? JSON.parse(startAttr) : { d: 2, h: 14, m: 37, s: 52 };
      } catch (_) {
        state = { d: 2, h: 14, m: 37, s: 52 };
      }

      const apply = () => {
        const setIf = (key, val) => {
          const el = root.querySelector(`[data-cd="${key}"]`);
          if (el) el.textContent = pad(val);
        };
        setIf('d', state.d);
        setIf('h', state.h);
        setIf('m', state.m);
        setIf('s', state.s);
      };
      apply();
      setInterval(() => { state = tick(state); apply(); }, 1000);
    });
  }

  /* ------------------------------------------ mobile sticky bar on scroll  */
  function initMobileStickyBar() {
    const bar    = $('[data-mobile-sticky]');
    const spacer = $('[data-mobile-sticky-spacer]');
    if (!bar) return;
    let visible = false;
    const update = () => {
      const next = window.scrollY > 500;
      if (next === visible) return;
      visible = next;
      bar.dataset.visible = String(visible);
      if (spacer) {
        spacer.style.height = visible ? '8rem' : '0';
      }
    };
    window.addEventListener('scroll', update, { passive: true });
    update();

    $$('[data-scroll-to]', bar).forEach((btn) => {
      btn.addEventListener('click', (e) => {
        const target = document.getElementById(btn.getAttribute('data-scroll-to'));
        if (target) {
          e.preventDefault();
          target.scrollIntoView({ behavior: 'smooth' });
        }
      });
    });
  }

  /* ----------------------------------------------------- FAQ accordion     */
  function initFaqAccordion() {
    const close = (list, idx) => {
      const item = list.querySelector(`[data-faq-item="${idx}"]`);
      const body = list.querySelector(`[data-faq-body="${idx}"]`);
      const chev = list.querySelector(`[data-faq-chev="${idx}"]`);
      if (!item || !body) return;
      body.dataset.open = 'false';
      if (chev) chev.dataset.open = 'false';
      item.classList.remove('border-green-300', 'shadow-lg', 'shadow-green-100', 'ring-1', 'ring-green-200');
      item.classList.add('border-gray-200', 'shadow-sm');
    };

    const open = (list, idx) => {
      const item = list.querySelector(`[data-faq-item="${idx}"]`);
      const body = list.querySelector(`[data-faq-body="${idx}"]`);
      const chev = list.querySelector(`[data-faq-chev="${idx}"]`);
      if (!item || !body) return;
      body.dataset.open = 'true';
      if (chev) chev.dataset.open = 'true';
      item.classList.remove('border-gray-200', 'shadow-sm');
      item.classList.add('border-green-300', 'shadow-lg', 'shadow-green-100', 'ring-1', 'ring-green-200');
    };

    $$('[data-faq-list]').forEach((list) => {
      if (list.dataset.faqReady === 'true') return;
      list.dataset.faqReady = 'true';

      list.addEventListener('click', (event) => {
        const trigger = event.target.closest('[data-faq-trigger]');
        if (!trigger || !list.contains(trigger)) return;
        const idx = trigger.getAttribute('data-faq-trigger');
        const body = list.querySelector(`[data-faq-body="${idx}"]`);
        if (!body) return;

        const isOpen = body.dataset.open === 'true';
        $$('[data-faq-body]', list).forEach((b) => close(list, b.getAttribute('data-faq-body')));
        if (!isOpen) open(list, idx);
      });

      const openIndex = Number(list.getAttribute('data-open-index') ?? '1');
      if (openIndex >= 0) open(list, String(openIndex));
    });
  }

  /* ------------------------------------------------ live order ticker     */
  function initTicker() {
    $$('[data-ticker]').forEach((root) => {
      if (root.dataset.tickerReady === 'true') return;
      const cells = $$('[data-ticker-cell]', root);
      if (cells.length === 0) return;
      const track = $('[data-ticker-track]', root);
      let i = Math.floor(Math.random() * cells.length);
      const randomMinutes = () => `${Math.floor(Math.random() * 62) + 28} menit lalu`;

      const show = (next) => {
        if (track) track.style.transform = `translateX(-${next * 100}%)`;
        cells.forEach((c, idx) => (c.dataset.active = String(idx === next)));
        const time = $('[data-order-time]', cells[next]);
        if (time) time.textContent = randomMinutes();
      };

      root.dataset.tickerReady = 'true';
      show(i);
      if (cells.length < 2) return;

      setInterval(() => {
        let next = i;
        while (next === i) next = Math.floor(Math.random() * cells.length);
        i = next;
        show(i);
      }, 6500);
    });
  }

  /* ----------------------------------------- customer service hub (CS)   */
  function initCustomerServiceHub() {
    const root = $('[data-csh]');
    if (!root) return;
    const fab        = $('[data-csh-fab]', root);
    const panel      = $('[data-csh-panel]', root);
    const tooltip    = $('[data-csh-tooltip]', root);
    const dismissTip = $('[data-csh-tooltip-close]', root);
    const closeBtn   = $('[data-csh-close]', root);
    const openTriggers = $$('[data-csh-open]');

    let tooltipDismissed = false;
    const openPanel = () => {
      panel.dataset.open = 'true';
      if (tooltip) tooltip.dataset.open = 'false';
      tooltipDismissed = true;
    };
    const closePanel = () => { panel.dataset.open = 'false'; };
    const togglePanel = () => (panel.dataset.open === 'true' ? closePanel() : openPanel());

    fab.addEventListener('click', togglePanel);
    openTriggers.forEach((trigger) => {
      trigger.addEventListener('click', (event) => {
        event.preventDefault();
        openPanel();
      });
    });
    if (dismissTip) {
      dismissTip.addEventListener('click', () => {
        if (tooltip) tooltip.dataset.open = 'false';
        tooltipDismissed = true;
      });
    }
    if (closeBtn) closeBtn.addEventListener('click', closePanel);
    setTimeout(() => {
      if (tooltip && !tooltipDismissed && panel.dataset.open !== 'true') tooltip.dataset.open = 'true';
    }, 4000);
  }

  /* ----------------------------------------- WhatsApp bubble (FAQ page)  */
  function initWhatsappBubble() {
    const root = $('[data-wa-bubble]');
    if (!root) return;
    const bubble = $('[data-wa-bubble-card]', root);
    const close  = $('[data-wa-bubble-close]', root);
    if (!bubble) return;
    let dismissed = false;
    const show = () => { if (!dismissed) bubble.dataset.open = 'true'; };
    const hide = () => { bubble.dataset.open = 'false'; };
    setTimeout(show, 3000);
    if (close) close.addEventListener('click', () => { hide(); dismissed = true; });
  }

  /* ----------------------------------------- smooth anchor scroll helper */
  function initAnchorScroll() {
    $$('a[href^="#"]').forEach((a) => {
      a.addEventListener('click', (e) => {
        const id = a.getAttribute('href');
        if (!id || id === '#') return;
        const target = document.querySelector(id);
        if (target) {
          e.preventDefault();
          target.scrollIntoView({ behavior: 'smooth' });
        }
      });
    });
  }

  /* ----------------------------------------------- Shopify cart helpers */
  const formatMoney = (cents) => {
    const amount = Number(cents || 0) / 100;
    try {
      return new Intl.NumberFormat('id-ID', {
        style: 'currency',
        currency: (window.Shopify && Shopify.currency && Shopify.currency.active) || 'IDR',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(amount);
    } catch (_) {
      return `Rp ${Math.round(amount).toLocaleString('id-ID')}`;
    }
  };

  async function postJson(url, body) {
    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error('Shopify request failed');
    return res.json();
  }

  function updateCartCount(count) {
    $$('[data-cart-count]').forEach((el) => { el.textContent = String(count); });
  }

  async function refreshCartCount() {
    try {
      const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
      updateCartCount(cart.item_count || 0);
      return cart;
    } catch (_) {
      return null;
    }
  }

  /* ----------------------------------------------- product add/buy now */
  function initProductForms() {
    $$('[data-product-form], [data-product-card-form]').forEach((form) => {
      form.addEventListener('submit', async (e) => {
        if (e.submitter && e.submitter.matches('[data-buy-now]')) return;
        e.preventDefault();
        const button = $('[data-add-to-cart-button]', form);
        if (button && button.disabled) return;
        const original = button ? button.textContent : '';
        if (button) {
          button.disabled = true;
          button.textContent = 'Menambahkan...';
        }
        try {
          const data = new FormData(form);
          await postJson('/cart/add.js', {
            id: String(data.get('id')),
            quantity: Number(data.get('quantity') || 1),
          });
          await refreshCartCount();
          if (button) button.textContent = 'Ditambahkan';
        } catch (_) {
          if (button) button.textContent = 'Gagal, coba lagi';
        } finally {
          setTimeout(() => {
            if (button) {
              button.disabled = false;
              button.textContent = original;
            }
          }, 1200);
        }
      });

      const buyNow = $('[data-buy-now]', form);
      if (buyNow) {
        buyNow.addEventListener('click', async () => {
          if (buyNow.disabled) return;
          buyNow.textContent = 'Memproses...';
        });
      }
    });
  }

  /* ----------------------------------------------- product gallery */
  function initProductGallery() {
    const root = $('[data-product-gallery]');
    if (!root) return;
    const main = $('[data-product-gallery-main]', root);
    const thumbs = $$('[data-gallery-thumb]');
    if (!main || thumbs.length === 0) return;
    let index = 0;

    const show = (next) => {
      index = (next + thumbs.length) % thumbs.length;
      const thumb = thumbs[index];
      main.setAttribute('src', thumb.getAttribute('data-gallery-src'));
      main.setAttribute('alt', thumb.getAttribute('data-gallery-alt') || '');
      thumbs.forEach((el, i) => {
        el.classList.toggle('border-amber-400', i === index);
        el.classList.toggle('ring-2', i === index);
        el.classList.toggle('ring-amber-200', i === index);
      });
    };

    thumbs.forEach((thumb, i) => thumb.addEventListener('click', () => show(i)));
    const prev = $('[data-gallery-prev]', root);
    const next = $('[data-gallery-next]', root);
    if (prev) prev.addEventListener('click', () => show(index - 1));
    if (next) next.addEventListener('click', () => show(index + 1));
    show(0);
  }

  /* ----------------------------------------------- cart page ajax */
  function initCartAjax() {
    const form = $('[data-cart-form]');
    if (!form) return;

    const applyCart = (cart) => {
      updateCartCount(cart.item_count || 0);
      const itemsByKey = new Map((cart.items || []).map((item) => [item.key, item]));
      $$('[data-cart-item]', form).forEach((row) => {
        const item = itemsByKey.get(row.getAttribute('data-key'));
        if (!item) {
          row.remove();
          return;
        }
        const qty = $('[data-cart-quantity]', row);
        const price = $('[data-cart-line-price]', row);
        if (qty) qty.value = item.quantity;
        if (price) price.textContent = formatMoney(item.final_line_price);
      });
      $$('[data-cart-subtotal], [data-cart-total]').forEach((el) => {
        el.textContent = formatMoney(cart.total_price);
      });
      if (!cart.item_count) window.location.reload();
    };

    const changeItem = async (row, quantity) => {
      const key = row.getAttribute('data-key');
      row.style.opacity = '0.6';
      try {
        const cart = await postJson('/cart/change.js', { id: key, quantity: Math.max(0, quantity) });
        applyCart(cart);
      } finally {
        row.style.opacity = '';
      }
    };

    $$('[data-cart-item]', form).forEach((row) => {
      const input = $('[data-cart-quantity]', row);
      const minus = $('[data-cart-qty-minus]', row);
      const plus = $('[data-cart-qty-plus]', row);
      const remove = $('[data-cart-remove]', row);
      if (minus && input) minus.addEventListener('click', () => changeItem(row, Number(input.value || 0) - 1));
      if (plus && input) plus.addEventListener('click', () => changeItem(row, Number(input.value || 0) + 1));
      if (input) input.addEventListener('change', () => changeItem(row, Number(input.value || 0)));
      if (remove) remove.addEventListener('click', () => changeItem(row, 0));
    });

    const update = $('[data-cart-update]', form);
    if (update) update.addEventListener('click', async () => {
      const updates = {};
      $$('[data-cart-item]', form).forEach((row) => {
        const input = $('[data-cart-quantity]', row);
        if (input) updates[row.getAttribute('data-key')] = Math.max(0, Number(input.value || 0));
      });
      const cart = await postJson('/cart/update.js', { updates });
      applyCart(cart);
    });

    const checkout = $('[data-cart-checkout]', form);
    if (checkout) checkout.addEventListener('click', () => {
      setTimeout(() => { window.location.href = '/checkout'; }, 50);
    });
  }

  /* --------------------------------------------------- hero video modal */
  function initVideoModal() {
    const modal = $('[data-video-modal]');
    const open = $('[data-video-open]');
    if (!modal || !open || modal.dataset.ready === 'true') return;
    const video = $('[data-video-player]', modal);
    const close = () => {
      modal.hidden = true;
      modal.style.display = '';
      document.documentElement.style.overflow = '';
      if (video) video.pause();
    };
    open.addEventListener('click', (event) => {
      event.preventDefault();
      modal.hidden = false;
      modal.style.display = 'flex';
      document.documentElement.style.overflow = 'hidden';
      if (video) video.play().catch(() => {});
    });
    $$('[data-video-close]', modal).forEach((button) => button.addEventListener('click', close));
    modal.addEventListener('click', (event) => {
      if (event.target === modal) close();
    });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !modal.hidden) close();
    });
    modal.dataset.ready = 'true';
  }

  /* --------------------------------------------- review image lightbox */
  function initReviewImageModal() {
    const modal = $('[data-review-image-modal]');
    if (!modal || modal.dataset.ready === 'true') return;
    const viewer = $('[data-review-image-viewer]', modal);
    const close = () => {
      modal.hidden = true;
      modal.style.display = '';
      document.documentElement.style.overflow = '';
      if (viewer) viewer.removeAttribute('src');
    };
    $$('[data-review-image]').forEach((img) => {
      img.addEventListener('click', (event) => {
        event.preventDefault();
        if (!viewer) return;
        viewer.src = img.currentSrc || img.src;
        viewer.alt = img.alt || '';
        modal.hidden = false;
        modal.style.display = 'flex';
        document.documentElement.style.overflow = 'hidden';
      });
    });
    $$('[data-review-image-close]', modal).forEach((button) => button.addEventListener('click', close));
    modal.addEventListener('click', (event) => {
      if (event.target === modal) close();
    });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !modal.hidden) close();
    });
    modal.dataset.ready = 'true';
  }

  /* ----------------------------------------- mark body ready after paint */
  function markBootReady() {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => document.body.classList.add('boot-ready'));
    });
  }

  onReady(() => {
    initMobileMenu();
    initCountdowns();
    initMobileStickyBar();
    initFaqAccordion();
    initTicker();
    initCustomerServiceHub();
    initWhatsappBubble();
    initAnchorScroll();
    initProductForms();
    initProductGallery();
    initCartAjax();
    initVideoModal();
    initReviewImageModal();
    markBootReady();
  });

  document.addEventListener('shopify:section:load', () => {
    initFaqAccordion();
    initTicker();
    initProductGallery();
    initCartAjax();
    initVideoModal();
    initReviewImageModal();
  });
})();
